import { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";

/**
 * The signed-in user's status line ("activity"): its local state, the listener that keeps it in sync with
 * other parts of the app (the `echo:activity-updated` event), and the action that saves a new one.
 */
export function useUserStatusMessage() {
  const [userStatusMessage, setUserStatusMessage] = useState<string>(() => {
    try {
      return localStorage.getItem('profile.activity') || '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleActivityUpdate = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (typeof detail === "string") {
        setUserStatusMessage(detail);
      } else {
        try {
          setUserStatusMessage(localStorage.getItem('profile.activity') || '');
        } catch {
          setUserStatusMessage('');
        }
      }
    };
    window.addEventListener('echo:activity-updated', handleActivityUpdate as EventListener);
    return () => {
      window.removeEventListener('echo:activity-updated', handleActivityUpdate as EventListener);
    };
  }, []);
  const updateUserStatusMessage = useCallback(async (nextMessage: string) => {
    const normalized = nextMessage.replace(/\s+/g, ' ').trim().slice(0, 160);
    try {
      const tok = getAuthToken() || '';
      await api.patchAuth('/users/me', { activity: normalized || null }, tok);
      setUserStatusMessage(normalized);
      try {
        localStorage.setItem('profile.activity', normalized);
        const raw = localStorage.getItem('user');
        const prev = raw ? JSON.parse(raw) : {};
        localStorage.setItem('user', JSON.stringify({ ...prev, activity: normalized }));
      } catch {
        /* noop */
      }
      try {
        window.dispatchEvent(new CustomEvent('echo:activity-updated', { detail: normalized }));
      } catch {
        /* noop */
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to update status';
      toast(msg || 'Failed to update status', 'error');
      throw e;
    }
  }, []);
  return { userStatusMessage, updateUserStatusMessage };
}
